// useWebsiteFormLogic.ts
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import navigateToWebsite from "0.Mine/1.Hooks/utils/navigation";
import { Business } from "0.Mine/3.Interfaces/business";
import ArgonSnackbar from "components/ArgonSnackbar";

export interface WebsiteFormLogicProps {
  initialValue: string;
  updateBusinessData: (data: Partial<Business>) => void;
}

export interface WebsiteFormLogicReturn {
  localValue: string;
  handleChange: (value: string) => void;
  handleBlur: () => void;
  snackbar: {
    open: boolean;
    color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark" | "light";
    icon: string;
    title: string;
    content: string;
    dateTime: string;
    onClose: () => void;
  };
}

export const useWebsiteFormLogic = ({
  initialValue,
  updateBusinessData,
}: WebsiteFormLogicProps): WebsiteFormLogicReturn => {
  const [localValue, setLocalValue] = useState(initialValue);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "info" as
      | "primary"
      | "secondary"
      | "info"
      | "success"
      | "warning"
      | "error"
      | "dark"
      | "light",
    icon: "notifications",
    title: t("onboarding.websiteVerification"),
    content: "",
    dateTime: new Date().toLocaleTimeString(),
  });

  const handleChange = useCallback((value: string) => {
    setLocalValue(value);
  }, []);

  const showSnackbar = useCallback(
    (
      content: string,
      color:
        | "primary"
        | "secondary"
        | "info"
        | "success"
        | "warning"
        | "error"
        | "dark"
        | "light" = "info",
      title: string = "Website Verification"
    ) => {
      setSnackbar({
        open: true,
        color,
        icon: color === "error" || color === "warning" ? "error" : "notifications",
        title,
        content,
        dateTime: new Date().toLocaleTimeString(),
      });

      // Auto-hide after 3 seconds
      setTimeout(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
      }, 3000);
    },
    []
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleBlur = useCallback(async () => {
    try {
      const result = await navigateToWebsite(localValue);
      if (result.success && result.url) {
        updateBusinessData({ websiteUrl: result.url });
        showSnackbar("Website verified successfully!", "success");
      } else {
        showSnackbar("Website hasn't been found!", "warning");
      }
    } catch (error) {
      showSnackbar("Error verifying website", "error");
    }
  }, [localValue, updateBusinessData, showSnackbar]);

  return {
    localValue,
    handleChange,
    handleBlur,
    snackbar: {
      ...snackbar,
      onClose: handleCloseSnackbar,
    },
  };
};
